<template>
  <!-- Add / Edit Payroll Deduction -->
  <div class="modal fade dq-payroll-modal" id="add_deduction" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-md">
      <div class="modal-content dq-modal-content">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="dq-modal-titlewrap">
            <div class="dq-hero-icon dq-hero-red">
              <i class="ti ti-arrow-down"></i>
            </div>
            <div>
              <h4 class="modal-title dq-modal-title mb-0">
                {{ isEditMode ? "Edit Deduction" : "Add Deduction" }}
              </h4>
              <div class="dq-modal-sub">
                Configure salary deductions and employee assignment.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            @click="resetForm"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <!-- Body -->
        <form @submit.prevent="onSubmit">
          <div class="modal-body dq-modal-body pb-0">
            <!-- BASIC INFO -->
            <div class="dq-section">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-badge dq-badge-red">
                    <i class="ti ti-info-circle"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Basic Information</div>
                    <div class="dq-help">
                      Name and default deduction configuration.
                    </div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="row g-3">
                  <div class="col-md-12">
                    <label class="form-label dq-label">Name</label>
                    <input
                      v-model="form.name"
                      type="text"
                      class="form-control dq-input"
                      placeholder="e.g. Advance, Absent amount"
                      required
                    />
                  </div>

                  <div class="col-md-8">
                    <label class="form-label dq-label">
                      Default / Unit Amount
                    </label>
                    <input
                      v-model.number="form.amount"
                      type="number"
                      min="0"
                      step="0.01"
                      class="form-control dq-input"
                      placeholder="Enter amount e.g. 2000"
                      required
                    />
                  </div>

                  <div class="col-md-4">
                    <div class="dq-switch-card">
                      <div>
                        <div class="dq-switch-title">Unit Calculation</div>
                        <div class="dq-help mb-0">
                          Multiply by number of units
                        </div>
                      </div>
                      <div class="form-check form-switch m-0">
                        <input
                          class="form-check-input dq-switch"
                          type="checkbox"
                          v-model="form.is_unit_calculation"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ASSIGNEE -->
            <div class="dq-section">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-badge dq-badge-purple">
                    <i class="ti ti-users"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Assignee</div>
                    <div class="dq-help">
                      Choose who this deduction applies to.
                    </div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="dq-radio-grid">
                  <label class="dq-radio">
                    <input
                      class="form-check-input"
                      type="radio"
                      value="none"
                      v-model="form.assignee_type"
                    />
                    <span class="dq-radio-ui">
                      <i class="ti ti-minus"></i>
                      <span>
                        <span class="dq-radio-title">No assignee</span>
                        <span class="dq-radio-sub">
                          Deduction is optional per payroll
                        </span>
                      </span>
                    </span>
                  </label>

                  <label class="dq-radio">
                    <input
                      class="form-check-input"
                      type="radio"
                      value="all"
                      v-model="form.assignee_type"
                    />
                    <span class="dq-radio-ui">
                      <i class="ti ti-stack-2"></i>
                      <span>
                        <span class="dq-radio-title">All employees</span>
                        <span class="dq-radio-sub">
                          Applies to everyone automatically
                        </span>
                      </span>
                    </span>
                  </label>

                  <label class="dq-radio">
                    <input
                      class="form-check-input"
                      type="radio"
                      value="select"
                      v-model="form.assignee_type"
                    />
                    <span class="dq-radio-ui">
                      <i class="ti ti-user-plus"></i>
                      <span>
                        <span class="dq-radio-title">Select employees</span>
                        <span class="dq-radio-sub">
                          Choose specific employees
                        </span>
                      </span>
                    </span>
                  </label>
                </div>

                <!-- EMPLOYEE SELECT -->
                <div
                  v-if="form.assignee_type === 'select'"
                  class="mt-3"
                >
                  <label class="form-label dq-label">
                    Select Employee(s)
                  </label>

                  <div
                    class="dropdown w-100 dq-dropdown-wrap"
                    data-bs-auto-close="outside"
                    data-bs-display="static"
                  >
                    <button
                      class="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center dq-dropdown-btn"
                      type="button"
                      data-bs-toggle="dropdown"
                    >
                      <span class="text-truncate d-flex align-items-center gap-2">
                        <i class="ti ti-users"></i>
                        <span class="text-truncate">
                          {{ selectedEmployeesLabel }}
                        </span>
                      </span>
                      <i class="ti ti-chevron-down ms-2"></i>
                    </button>

                    <div
                      class="dropdown-menu p-2 w-100 shadow dq-dropdown"
                      @click.stop
                    >
                      <div class="dq-search">
                        <i class="ti ti-search"></i>
                        <input
                          v-model="employeeQuery"
                          type="text"
                          class="form-control dq-search-input"
                          placeholder="Search employees..."
                          autocomplete="off"
                        />
                      </div>

                      <div
                        class="dq-list"
                        style="max-height: 240px; overflow-y: auto"
                      >
                        <button
                          v-for="emp in filteredEmployees"
                          :key="emp.id"
                          type="button"
                          class="dropdown-item dq-item d-flex justify-content-between align-items-center"
                          @click="toggleEmployee(emp.id)"
                        >
                          <span class="text-truncate">
                            {{ emp.first_name }} {{ emp.last_name || "" }}
                            <span class="dq-muted">
                              ({{ emp.employee_id }})
                            </span>
                          </span>

                          <span
                            v-if="isEmployeeSelected(emp.id)"
                            class="dq-chip"
                          >
                            <i class="ti ti-check me-1"></i> Selected
                          </span>
                        </button>

                        <div
                          v-if="filteredEmployees.length === 0"
                          class="text-center py-2 small text-muted"
                        >
                          No employee found
                        </div>
                      </div>

                      <div
                        class="dq-drop-footer"
                        v-if="form.employee_ids.length"
                      >
                        <span class="dq-help mb-0">
                          {{ form.employee_ids.length }} selected
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Alerts -->
            <div v-if="error" class="alert alert-danger my-2 dq-alert">
              {{ error }}
            </div>
            <div v-if="success" class="alert alert-success my-2 dq-alert">
              {{ success }}
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer dq-modal-footer">
            <button
              type="button"
              class="btn btn-white border me-2 dq-btn-muted"
              data-bs-dismiss="modal"
              @click="resetForm"
            >
              Cancel
            </button>
            <button type="submit" class="btn btn-primary dq-btn-primary">
              <span
                v-if="deductionStore.isLoading"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              {{ isEditMode ? "Save Changes" : "Add Deduction" }}
              <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- DELETE MODAL -->
  <div
    class="modal fade dq-confirm-modal"
    id="deletedeductionModal"
    tabindex="-1"
    ref="deleteModalEl"
  >
    <div class="modal-dialog modal-dialog-centered modal-sm">
      <div class="modal-content dq-modal-content">
        <div class="modal-body text-center dq-confirm-body">
          <div class="dq-confirm-icon dq-confirm-red">
            <i class="ti ti-trash-x"></i>
          </div>
          <h4 class="mb-1 dq-confirm-title">Confirm Delete</h4>
          <p class="mb-3 dq-confirm-sub">
            This deduction will be permanently removed. This action cannot be
            undone.
          </p>

          <div class="d-flex justify-content-center gap-2">
            <button class="btn btn-light dq-btn-muted" data-bs-dismiss="modal">
              Cancel
            </button>
            <button
              class="btn btn-danger dq-btn-danger"
              @click="confirmDelete"
            >
              <span
                v-if="deductionStore.isLoading"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              Yes, Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, defineExpose } from "vue";
import { usePayrollDeductionStore } from "@/stores/payrollDeductionStore";
import { useEmployeeStore } from "@/stores/employeeStore";
import Modal from "bootstrap/js/dist/modal";

const deductionStore = usePayrollDeductionStore();
const employeeStore = useEmployeeStore();

const emit = defineEmits(["refresh"]);

const isEditMode = ref(false);

const form = reactive({
  id: null,
  name: "",
  amount: 0,
  is_unit_calculation: false,
  assignee_type: "none",
  employee_ids: [],
});

const error = ref("");
const success = ref("");
const employeeQuery = ref("");

// load employees
onMounted(() => {
  employeeStore.fetchEmployees({ status: "Active", per_page: 5000 });
});

// filtered list
const activeEmployees = computed(() =>
  employeeStore.employees.filter(
    (emp) => String(emp?.status || "").trim().toLowerCase() === "active",
  ),
);

const filteredEmployees = computed(() => {
  const q = employeeQuery.value.trim().toLowerCase();
  if (!q) return activeEmployees.value;
  return activeEmployees.value.filter((emp) => {
    const fullName = `${emp.first_name} ${emp.last_name || ""}`.toLowerCase();
    return (
      fullName.includes(q) ||
      (emp.employee_id || "").toLowerCase().includes(q)
    );
  });
});

// label
const selectedEmployeesLabel = computed(() => {
  if (!form.employee_ids.length) return "Select employees";

  const selected = activeEmployees.value.filter((e) =>
    isEmployeeSelected(e.id),
  );

  if (selected.length === 1) {
    const e = selected[0];
    return `${e.first_name} ${e.last_name || ""} (${e.employee_id})`;
  }

  return `${selected.length} employees selected`;
});

function isEmployeeSelected(id) {
  const normalized = Number(id);
  return form.employee_ids.some((x) => Number(x) === normalized);
}

// toggle
function toggleEmployee(id) {
  const normalized = Number(id);
  const idx = form.employee_ids.findIndex((x) => Number(x) === normalized);
  if (idx === -1) form.employee_ids.push(normalized);
  else form.employee_ids.splice(idx, 1);
}

function resetForm() {
  form.id = null;
  form.name = "";
  form.amount = 0;
  form.is_unit_calculation = false;
  form.assignee_type = "none";
  form.employee_ids = [];
  employeeQuery.value = "";
  isEditMode.value = false;
  error.value = "";
  success.value = "";
}

function closeModal(id) {
  const modalEl = document.getElementById(id);
  if (modalEl) {
    modalEl.classList.remove("show");
    modalEl.style.display = "none";
    modalEl.setAttribute("aria-hidden", "true");
    modalEl.removeAttribute("aria-modal");
  }
  document.body.classList.remove("modal-open");
  document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
}

async function onSubmit() {
  error.value = "";
  if (!form.name || !form.amount) {
    error.value = "Please fill in all required fields.";
    return;
  }
  if (form.assignee_type === "select" && !form.employee_ids.length) {
    error.value = "Please select at least one employee.";
    return;
  }

  const payload = {
    name: form.name,
    amount: form.amount,
    is_unit_calculation: form.is_unit_calculation,
    assignee_type: form.assignee_type,
    employee_ids:
      form.assignee_type === "select" ? form.employee_ids : [],
  };

  if (!isEditMode.value) {
    await deductionStore.createDeduction(payload);
  } else {
    await deductionStore.updateDeduction(form.id, payload);
  }

  emit("refresh");
  setTimeout(() => {
    closeModal("add_deduction");
    resetForm();
  }, 600);
}

async function confirmDelete() {
  await deductionStore.deleteDeduction(form.id);
  closeModal("deletedeductionModal");
  resetForm();
  emit("refresh");
}

function openForEdit(deduction) {
  form.id = deduction.id;
  form.name = deduction.name;
  form.amount = deduction.amount;
  form.is_unit_calculation = !!deduction.is_unit_calculation;
  form.assignee_type = deduction.assignee_type || "none";
  form.employee_ids = deduction.employee_ids || [];
  isEditMode.value = true;
}

function openForDelete(deduction) {
  form.id = deduction.id;
  const modal = new Modal(
    document.getElementById("deletedeductionModal"),
    { backdrop: "static" }
  );
  modal.show();
}

defineExpose({
  openForEdit,
  resetForm,
  openForDelete,
});
</script>

<style scoped>
/* ===== Shared premium styles (safe & scoped) ===== */
.dq-modal-content {
  border: 0;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 26px 70px rgba(16, 24, 40, 0.18);
}

.dq-modal-header {
  padding: 16px;
  background:
    radial-gradient(800px 260px at 10% 0%, rgba(239, 68, 68, 0.14), transparent 60%),
    linear-gradient(180deg, #f8fafc, #ffffff);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.dq-modal-titlewrap {
  display: flex;
  align-items: center;
  gap: 12px;
}

.dq-hero-icon {
  width: 46px;
  height: 46px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.dq-hero-red {
  background: rgba(239, 68, 68, 0.12);
  color: rgb(220, 38, 38);
}

.dq-modal-title {
  font-weight: 950;
  letter-spacing: -0.02em;
}

.dq-modal-sub {
  font-size: 0.82rem;
  color: rgba(17, 24, 39, 0.6);
}

.dq-modal-body {
  padding: 14px 16px;
}

.dq-modal-footer {
  padding: 12px 16px;
  background: #f8fafc;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.dq-section {
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 18px;
  background: #fff;
  margin-bottom: 12px;
}

.dq-section-head {
  padding: 12px 14px;
  background: #f8fafc;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.dq-section-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.dq-section-badge {
  width: 38px;
  height: 38px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.dq-badge-red {
  background: rgba(239, 68, 68, 0.12);
  color: rgb(220, 38, 38);
}

.dq-badge-purple {
  background: rgba(99, 102, 241, 0.12);
  color: rgb(79, 70, 229);
}

.dq-section-body {
  padding: 14px;
}

.dq-h6 {
  font-weight: 950;
}

.dq-help {
  font-size: 0.82rem;
  color: rgba(17, 24, 39, 0.6);
}

.dq-label {
  font-weight: 850;
}

.dq-input {
  border-radius: 14px;
  min-height: 44px;
}

.dq-switch-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border-radius: 16px;
  background: rgba(239, 68, 68, 0.06);
}

.dq-radio-grid {
  display: grid;
  gap: 10px;
}

.dq-radio {
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 16px;
  padding: 10px 12px;
  cursor: pointer;
}

.dq-radio-ui {
  display: flex;
  gap: 10px;
}

.dq-radio-title {
  font-weight: 900;
}

.dq-radio-sub {
  font-size: 0.8rem;
  color: rgba(17, 24, 39, 0.6);
}

.dq-dropdown-btn {
  border-radius: 14px;
  min-height: 44px;
}

.dq-chip {
  background: rgba(239, 68, 68, 0.12);
  color: rgb(220, 38, 38);
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 800;
}

.dq-btn-primary {
  border-radius: 14px;
}

.dq-btn-muted {
  border-radius: 14px;
}

.dq-confirm-icon {
  width: 64px;
  height: 64px;
  border-radius: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 10px;
  font-size: 26px;
}

.dq-confirm-red {
  background: rgba(239, 68, 68, 0.12);
  color: rgb(220, 38, 38);
}
</style>
